/**
 * Shared store for the "Action Failed" modal.
 *
 * Components fire user actions through `runAction(fn)`: it clears any prior
 * error before starting, awaits the call, and routes a thrown ActionError
 * (or anything else) into the modal via captureError. The modal lives in
 * <ActionErrorModal /> mounted at the dashboard root.
 *
 * Lives outside useRobotStore because this is UI state, not robot state.
 */

import { create } from 'zustand';
import { ActionError } from '@shared/lib/api/errors';

type Store = {
  actionError: ActionError | null;
  setActionError: (e: ActionError | null) => void;
  /** Capture any thrown value into the modal: ActionError → as-is, anything else → blank. */
  captureError: (err: unknown) => void;
  /** Run a user action: clears any prior error, awaits, captures into modal on throw. */
  runAction: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
};

export const useActionError = create<Store>((set, get) => ({
  actionError: null,
  setActionError: (actionError) => set({ actionError }),
  captureError: (err) => {
    if (err instanceof ActionError) {
      set({ actionError: err });
    } else {
      set({ actionError: new ActionError(null, null, 0) });
    }
  },
  runAction: async (fn) => {
    set({ actionError: null });
    try {
      return await fn();
    } catch (err) {
      get().captureError(err);
      return undefined;
    }
  },
}));
